import type { ActorRefFrom } from 'xstate';

import { UnauthenticatedRoutes } from '@/router/routes';
import { LogoutRequestedRootEvent } from '@/router/state/root/component/root-event';
import { RootStates } from '@/router/state/root/component/root-states';
import type { rootStateMachine } from '@/router/state/root/root-state-machine';

/**
 * Imperative bridge to the root XState actor for use outside React components.
 *
 * ## Why this exists
 *
 * The root state machine's `LOGOUT_REQUESTED` event is the single source of
 * truth for logging a user out — it runs `signOutCurrentUser`, which unregisters
 * push notifications, cancels+clears TanStack Query, clears localStorage, and
 * calls `Auth.signOut()`. UI code dispatches it via `useRootActorRef()`.
 *
 * Some logout triggers live outside React — most notably the axios response
 * interceptor, which needs to log the user out when a request returns 401
 * (refresh token effectively dead: truly expired, revoked, or rejected due to
 * unrecoverable clock skew). We can't call a React hook from there, so
 * `AppRoutes` registers the live actor ref on mount and the interceptor
 * dispatches through this singleton.
 *
 * ## What NOT to use this for
 *
 * Inside React components/hooks, use `useRootActorRef()` directly — this
 * singleton exists only to bridge the gap between the React/XState context
 * and code that lives outside it.
 */
type RootActorRef = ActorRefFrom<typeof rootStateMachine>;

let _actorRef: RootActorRef | null = null;

export const setRootActorRef = (actorRef: RootActorRef): void => {
  _actorRef = actorRef;
};

export const requestLogout = (): void => {
  if (!_actorRef) {
    // Router hasn't mounted yet — we have no graceful path, so fall back to a
    // hard reload of the sign-in page.
    window.location.replace(UnauthenticatedRoutes.Login);
    return;
  }
  _actorRef.send(new LogoutRequestedRootEvent());
};

/**
 * Returns true when the root machine is currently in the Authenticated state.
 * Callers outside React use this to avoid firing logout-related side effects
 * (toasts, navigation) for 401s that happen after the user is already logged
 * out — e.g. a request that was already in flight when the user clicked
 * "Log out" manually.
 */
export const isAuthenticated = (): boolean => {
  if (!_actorRef) return false;
  return _actorRef.getSnapshot().matches(RootStates.Authenticated);
};
